/**
 * Store de la messagerie interne.
 *
 * Une seule connexion WebSocket (`/ws/pexitalk/chat/`) pour tout le poste :
 * le backend pousse les événements de TOUS les fils dont l'utilisateur est
 * membre sur sa room personnelle. Le store est branché dès le login (App.vue)
 * pour que les notifications visuelles (badge sidebar, toast, notification
 * native) arrivent même quand la vue Messages n'est pas ouverte.
 */
import { defineStore } from 'pinia'
import {
  ChatAPI,
  type ChatMessage,
  type ChatUser,
  type Conversation,
} from '@/shared/api/chat'
import { useAuthStore } from '@/shared/stores/auth'
import { useToasts } from '@/shared/stores/toasts'
import { useNotifications } from '@/shared/composables/useNotifications'
import { useWebSocket } from '@/shared/composables/useWebSocket'

type ChatSocket = ReturnType<typeof useWebSocket>

export const useChatStore = defineStore('chat', {
  state: () => ({
    conversations: [] as Conversation[],
    messages: {} as Record<number, ChatMessage[]>,
    hasMore: {} as Record<number, boolean>,
    contacts: [] as ChatUser[],
    activeConversationId: null as number | null,
    /** Vrai quand la vue /chat est affichée (pas de toast dans ce cas). */
    viewVisible: false,
    typing: {} as Record<number, { userId: number; name: string; until: number }>,
    wsState: 'closed' as 'open' | 'closed' | 'reconnecting',
    loaded: false,
    _ws: null as ChatSocket | null,
  }),

  getters: {
    unreadTotal: (state) =>
      state.conversations
        .filter((c) => !c.is_muted)
        .reduce((sum, c) => sum + (c.unread_count || 0), 0),

    activeConversation: (state) =>
      state.conversations.find((c) => c.id === state.activeConversationId) || null,

    activeMessages: (state) =>
      state.activeConversationId ? state.messages[state.activeConversationId] || [] : [],
  },

  actions: {
    // ── Cycle de vie ─────────────────────────────────────────────────
    async init() {
      if (this._ws) return
      await this.refreshConversations()
      const ws = useWebSocket('/ws/pexitalk/chat/')
      ws.on('chat.message', (msg) => this.onIncomingMessage(msg.message))
      ws.on('chat.message_deleted', (msg) =>
        this.onMessageDeleted(msg.conversation_id, msg.message_id),
      )
      ws.on('chat.conversation_update', () => this.refreshConversations())
      ws.on('chat.conversation_deleted', (msg) => this.onConversationDeleted(msg.conversation_id))
      ws.on('chat.typing', (msg) => this.onTyping(msg))
      ws.onStateChange((s) => {
        this.wsState = s
        // Au retour de connexion, resynchronise (messages manqués hors-ligne).
        if (s === 'open' && this.loaded) this.refreshConversations()
      })
      ws.connect()
      this._ws = ws
    },

    teardown() {
      this._ws?.disconnect()
      this._ws = null
      this.$reset()
    },

    // ── Données ──────────────────────────────────────────────────────
    async refreshConversations() {
      try {
        this.conversations = await ChatAPI.conversations()
        this.loaded = true
      } catch {
        /* offline — le WS retentera */
      }
    },

    async loadContacts() {
      try {
        this.contacts = await ChatAPI.contacts()
      } catch {
        this.contacts = []
      }
    },

    async openConversation(id: number) {
      this.activeConversationId = id
      if (!this.messages[id]) {
        const page = await ChatAPI.messages(id)
        this.messages[id] = page.results
        this.hasMore[id] = page.has_more
      }
      await this.markRead(id)
    },

    async loadOlder(id: number) {
      const current = this.messages[id] || []
      if (!current.length || !this.hasMore[id]) return
      const page = await ChatAPI.messages(id, current[0].id)
      this.messages[id] = [...page.results, ...current]
      this.hasMore[id] = page.has_more
    },

    async markRead(id: number) {
      const conv = this.conversations.find((c) => c.id === id)
      if (conv) conv.unread_count = 0
      try {
        await ChatAPI.markRead(id)
      } catch {
        /* re-synchronisé au prochain refresh */
      }
    },

    async send(conversationId: number, body: string, attachmentId?: number) {
      const msg = await ChatAPI.sendMessage(conversationId, body, attachmentId)
      this.appendMessage(msg) // echo local immédiat (le WS dédoublonne)
      return msg
    },

    sendTyping(conversationId: number) {
      const auth = useAuthStore()
      this._ws?.send({
        action: 'typing',
        conversation_id: conversationId,
        display_name: auth.displayName,
      })
    },

    // ── Événements entrants ──────────────────────────────────────────
    appendMessage(msg: ChatMessage) {
      const list = this.messages[msg.conversation_id]
      if (list && !list.some((m) => m.id === msg.id)) list.push(msg)
      const conv = this.conversations.find((c) => c.id === msg.conversation_id)
      if (conv) {
        conv.last_message = msg
        conv.updated_at = msg.created_at
        // remonte le fil en tête de liste
        this.conversations = [
          conv,
          ...this.conversations.filter((c) => c.id !== conv.id),
        ]
      }
    },

    onIncomingMessage(msg: ChatMessage) {
      const auth = useAuthStore()
      const own = msg.sender_id === auth.user?.id
      const conv = this.conversations.find((c) => c.id === msg.conversation_id)
      if (!conv) {
        // Nouveau fil (ex. quelqu'un vient de nous écrire) → resynchronise.
        this.refreshConversations()
      }
      this.appendMessage(msg)
      delete this.typing[msg.conversation_id]

      const isActive =
        this.viewVisible && this.activeConversationId === msg.conversation_id
      if (own) return
      if (isActive) {
        this.markRead(msg.conversation_id)
        return
      }
      if (conv) conv.unread_count = (conv.unread_count || 0) + 1
      if (conv?.is_muted) return

      // Notification visuelle : toast in-app + notification native (Electron).
      const title = conv
        ? conv.kind === 'direct'
          ? msg.sender_name
          : `${msg.sender_name} — ${conv.display_name}`
        : msg.sender_name
      const preview = msg.attachment
        ? `📎 ${msg.attachment.filename}`
        : msg.body.slice(0, 120)
      useToasts().info(`💬 ${title}`, preview)
      if (document.hidden) useNotifications().show(title, preview)
    },

    onMessageDeleted(conversationId: number, messageId: number) {
      const list = this.messages[conversationId]
      if (!list) return
      const msg = list.find((m) => m.id === messageId)
      if (msg) {
        msg.is_deleted = true
        msg.body = ''
        msg.attachment = null
      }
    },

    onTyping(msg: { conversation_id: number; user_id: number; display_name: string }) {
      this.typing[msg.conversation_id] = {
        userId: msg.user_id,
        name: msg.display_name,
        until: Date.now() + 4000,
      }
      setTimeout(() => {
        const t = this.typing[msg.conversation_id]
        if (t && t.until <= Date.now()) delete this.typing[msg.conversation_id]
      }, 4200)
    },

    // ── Actions de fil ───────────────────────────────────────────────
    async deleteMessage(messageId: number, conversationId: number) {
      await ChatAPI.deleteMessage(messageId)
      this.onMessageDeleted(conversationId, messageId)
    },

    async setArchived(conversationId: number, archived: boolean) {
      await ChatAPI.setArchived(conversationId, archived)
      const conv = this.conversations.find((c) => c.id === conversationId)
      if (conv) conv.is_archived = archived
    },

    async setMuted(conversationId: number, muted: boolean) {
      await ChatAPI.setMuted(conversationId, muted)
      const conv = this.conversations.find((c) => c.id === conversationId)
      if (conv) conv.is_muted = muted
    },

    async deleteConversation(conversationId: number) {
      await ChatAPI.deleteConversation(conversationId)
      this.onConversationDeleted(conversationId)
    },

    // Retire un fil supprimé (action locale OU événement WS d'un autre client).
    onConversationDeleted(conversationId: number) {
      this.conversations = this.conversations.filter((c) => c.id !== conversationId)
      delete this.messages[conversationId]
      if (this.activeConversationId === conversationId) this.activeConversationId = null
    },
  },
})
